import * as React from 'react'
import { useEffect, useRef, useState, useMemo } from 'react'

import { useInternalTransfersList } from './hooks'
import { InternalTransfer } from './types'
import {
  Button,
  Divider,
  InputRef,
  Spin,
  Table,
  Tag,
  Typography,
  Flex,
  message,
} from 'antd'
import {
  CopyOutlined,
  DownloadOutlined,
  SwapOutlined,
} from '@ant-design/icons'
import { path, role } from '../../../../libs'
import {
  buttonColorInternalTransferStatus,
  colorInternalTransferStatus,
  humanInternalTransferStatus,
  internalTransferStatus,
  statusInternalTransferFlow,
} from '../../const'
import { ColumnsType } from 'antd/lib/table'
import { FilterValue } from 'antd/es/table/interface'
import { getColumnSearchProps } from '../../../../libs/tableFunctions'
import { Guard } from '../../../../features/session/guard'
import { formatDigits } from '../../../../libs/utils'
import { submitStatus } from './model'
import { RollbackModal } from '../atoms/rollbackModal'
import { RejectModal } from '../atoms/rejectModal'
import { $userRole } from '../../../../features/user'
import { useStore } from 'effector-react'
import { useUnit } from 'effector-react'
import { InternalTransferEdit } from './types'
import {
  loadPurses,
  resetPurses,
  $sourcePurse,
  $destinationPurse,
  getSourcePurse,
  getDestinationPurse,
} from './model'

const { Text } = Typography

/* ── Shared styles ── */
const sectionTitle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--crm-text-secondary)',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  marginBottom: 10,
}

const infoRow: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'baseline',
  padding: '5px 0',
  borderBottom: '1px solid var(--crm-border)',
}

const infoLabel: React.CSSProperties = {
  fontSize: 16,
  color: 'var(--crm-text-secondary)',
  flexShrink: 0,
  marginRight: 8,
}

const infoValue: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 500,
  textAlign: 'right',
  maxWidth: '65%',
  wordBreak: 'break-all',
}

/* ── Detail Panel ── */
function DetailPanel({ record }: { record: InternalTransfer }) {
  const userRole = useStore($userRole)
  const sourcePurseData = useUnit($sourcePurse)
  const destPurseData = useUnit($destinationPurse)
  const sourceLoading = useUnit(getSourcePurse.pending)
  const destLoading = useUnit(getDestinationPurse.pending)

  useEffect(() => {
    resetPurses()
    loadPurses(record)
  }, [record.transferId])

  const isIn =
    record.client.accounts.some((a) => a.accountId == record.destinationId) ||
    record.client.wallets.some((w) => w.walletId == record.destinationId)

  const asEdit = record as unknown as InternalTransferEdit

  const statusFlow = statusInternalTransferFlow(record.status)

  const actionLabel = (status: string) => {
    if (status === internalTransferStatus.completed()) return 'Accept'
    return humanInternalTransferStatus(status)
  }

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <Flex align="center" justify="space-between" style={{ marginBottom: 16 }}>
        <Flex align="center" gap={8}>
          <SwapOutlined style={{ fontSize: 18 }} />
          <span style={{ fontSize: 18, fontWeight: 600 }}>
            Transfer {record.transferId}
          </span>
          <Tag color={isIn ? 'success' : 'processing'} style={{ fontSize: 10 }}>
            {isIn ? 'IN' : 'OUT'}
          </Tag>
        </Flex>
        <Tag
          color={colorInternalTransferStatus(record.status)}
          style={{ fontSize: 11 }}
        >
          {humanInternalTransferStatus(record.status)}
        </Tag>
      </Flex>

      {/* Actions */}
      {statusFlow && statusFlow.length > 0 && (
        <Flex gap={6} wrap="wrap" style={{ marginBottom: 16 }}>
          {statusFlow.map((item) => {
            if (item === internalTransferStatus.rollingBack()) {
              return <RollbackModal key={item} className="ms-2" request={asEdit} />
            }
            if (item === internalTransferStatus.rejected()) {
              return <RejectModal key={item} className="ms-2" request={asEdit} />
            }
            return (
              <Button
                key={item}
                hidden={
                  Array.isArray(userRole) &&
                  [
                    role.partnership.seniorPartnerManager(),
                    role.partnership.juniorPartnerManager(),
                  ].some((r) => userRole.includes(r))
                }
                style={{
                  color: buttonColorInternalTransferStatus(item).text,
                  backgroundColor: buttonColorInternalTransferStatus(item).background,
                  minWidth: 80,
                }}
                onClick={() => submitStatus({ transferId: record.transferId, status: item })}
              >
                {actionLabel(item)}
              </Button>
            )
          })}
        </Flex>
      )}

      {/* Client */}
      <div style={sectionTitle}>Client</div>
      <div
        style={{
          background: 'var(--crm-bg-elevated)',
          border: '1px solid var(--crm-border)',
          borderRadius: 8,
          padding: 14,
          marginBottom: 16,
        }}
      >
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 4 }}>
          {record.client.fullName}
        </div>
        <div style={{ fontSize: 18, color: 'var(--crm-text-secondary)', marginBottom: 6, wordBreak: 'break-all' }}>
          {record.client.email}
        </div>
        <Flex justify="space-between" align="center">
          <a
            href={path.clients.details(record.client.clientId)}
            target="_blank"
            style={{ fontSize: 18 }}
          >
            {record.client.clientId} ↗
          </a>
          <Flex gap={4} align="center">
            {record.client.isTest && (
              <Tag color="purple" style={{ fontSize: 9 }}>Test</Tag>
            )}
          </Flex>
        </Flex>
      </div>

      {/* FROM */}
      <div style={sectionTitle}>FROM ({record.sourceType === 'wallet' ? 'Wallet' : 'Account'})</div>
      <div style={{ marginBottom: 16 }}>
        <div style={infoRow}>
          <span style={infoLabel}>ID</span>
          <span style={infoValue}>{record.sourceId}</span>
        </div>
        <div style={infoRow}>
          <span style={infoLabel}>Amount</span>
          <span style={infoValue}>
            {formatDigits(Number(record.sourceAmount))} {record.sourceCurrency}
          </span>
        </div>
        <div style={{ ...infoRow, borderBottom: 'none' }}>
          <span style={infoLabel}>Balance</span>
          <span style={infoValue}>
            {sourceLoading ? (
              <Spin size="small" />
            ) : sourcePurseData ? (() => {
              const balanceLow = Number(sourcePurseData.balance) < Number(record.sourceAmount)
              return (
                <span style={{ color: balanceLow ? 'var(--crm-accent-red, #ef4444)' : 'var(--crm-accent-green, #22c55e)', fontWeight: 600 }}>
                  {formatDigits(Number(sourcePurseData.balance))} {sourcePurseData.currency}
                </span>
              )
            })() : '—'}
          </span>
        </div>
      </div>

      {/* Exchange rate */}
      <div style={{ textAlign: 'center', margin: '4px 0 12px', fontSize: 18, color: 'var(--crm-text-secondary)' }}>
        Exchange rate: <strong>{record.exchangeRate}</strong>
      </div>

      {/* TO */}
      <div style={sectionTitle}>TO ({record.destinationType === 'wallet' ? 'Wallet' : 'Account'})</div>
      <div style={{ marginBottom: 16 }}>
        <div style={infoRow}>
          <span style={infoLabel}>ID</span>
          <span style={infoValue}>{record.destinationId}</span>
        </div>
        <div style={infoRow}>
          <span style={infoLabel}>Amount</span>
          <span style={infoValue}>
            {formatDigits(Number(record.destinationAmount))} {record.destinationCurrency}
          </span>
        </div>
        <div style={{ ...infoRow, borderBottom: 'none' }}>
          <span style={infoLabel}>Balance</span>
          <span style={infoValue}>
            {destLoading ? (
              <Spin size="small" />
            ) : destPurseData ? (
              <span style={{ color: 'var(--crm-accent-green, #22c55e)', fontWeight: 600 }}>
                {formatDigits(Number(destPurseData.balance))} {destPurseData.currency}
              </span>
            ) : '—'}
          </span>
        </div>
      </div>

      {/* Details */}
      <Divider style={{ margin: '12px 0' }} />
      <div style={sectionTitle}>Details</div>
      <div style={infoRow}>
        <span style={infoLabel}>Commission</span>
        <span style={infoValue}>{record.commission}</span>
      </div>
      <div style={infoRow}>
        <span style={infoLabel}>Created</span>
        <span style={infoValue}>{record.createdAt}</span>
      </div>
      <div style={infoRow}>
        <span style={infoLabel}>Updated</span>
        <span style={infoValue}>{record.humanUpdatedAt}</span>
      </div>
      {record.manager?.userId && (
        <div style={infoRow}>
          <span style={infoLabel}>Manager</span>
          <span style={infoValue}>{record.manager.userId}</span>
        </div>
      )}
      {record.manualTransferReason && (
        <div style={infoRow}>
          <span style={infoLabel}>
            {record.manualTransferReasonVisibleForUser ? 'Reject reason' : 'Internal comment'}
          </span>
          <span style={infoValue}>{record.manualTransferReason}</span>
        </div>
      )}
      {record.internalComment && (
        <div style={infoRow}>
          <span style={infoLabel}>Internal comment</span>
          <span style={infoValue}>{record.internalComment}</span>
        </div>
      )}
      {record.failReason && (
        <div style={{ ...infoRow, borderBottom: 'none' }}>
          <span style={infoLabel}>Fail reason</span>
          <span style={{ ...infoValue, color: 'var(--crm-accent-red, #ef4444)' }}>
            {record.failReason}
          </span>
        </div>
      )}
    </div>
  )
}

/* ── Main component ── */
export const InternalTransferList = () => {
  const {
    internalTransfers,
    loading,
    totalCountOfInternalTransfers,
    onChangeInternalTransfersList,
  } = useInternalTransfersList()

  const [searchText, setSearchText] = useState<{ [key: string]: string }>({})
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({
    status: ['waiting_for_approve', 'pending_rollback'],
  })
  const [selectedRecord, setSelectedRecord] = useState<InternalTransfer | null>(null)

  const searchInput = useRef<InputRef>(null)

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) setPage(currentPage)
    if (currentPageSize) setPageSize(currentPageSize)
    if (currentOrderBy) setOrderBy(currentOrderBy)
    if (currentOrderDirection)
      setOrderDirection(currentOrderDirection === 'descend' ? '-' : '')
    if (filters) setFiltersState(filters)
  }

  useEffect(() => {
    onChangeInternalTransfersList({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      searchText,
      filters: filtersState,
    })
  }, [pageSize, page, orderBy, orderDirection, searchText, filtersState])

  const csvCode = useMemo(() => {
    const csvContent = internalTransfers
      .map((item) => Object.values(item).join(','))
      .join('\n')
    return 'data:text/csv;charset=utf-8,SEP=,%0A' + encodeURIComponent(csvContent)
  }, [internalTransfers])

  const columns: ColumnsType<InternalTransfer> = [
    {
      title: 'ID',
      dataIndex: 'transferId',
      width: 80,
      ...getColumnSearchProps<InternalTransfer>(
        'transferId',
        setSearchText,
        searchText,
        searchInput,
      ),
      sorter: true,
      defaultSortOrder: 'descend',
      render: (v: number) => (
        <Flex align="center" gap={4}>
          <Text style={{ fontSize: 18, fontWeight: 600 }}>{v}</Text>
        </Flex>
      ),
    },
    {
      title: 'User ID',
      key: 'clientId',
      width: 80,
      ...getColumnSearchProps<InternalTransfer>(
        'clientId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (_: any, item: InternalTransfer) => (
        <div>
          <Text style={{ fontSize: 18, fontWeight: 600 }}>
            {item.client.clientId}
          </Text>
          {item.client?.isTest && (
            <Tag color="purple" style={{ fontSize: 9, marginLeft: 4 }}>Test</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'From',
      key: 'from',
      width: 110,
      ...getColumnSearchProps<InternalTransfer>(
        'sourceId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (_: any, item: InternalTransfer) => (
        <Text style={{ fontSize: 18 }}>
          {item.sourceType === 'wallet' ? `Wallet ${item.sourceCurrency}` : `MT ${item.sourceId}`}
        </Text>
      ),
    },
    {
      title: 'To',
      key: 'to',
      width: 110,
      ...getColumnSearchProps<InternalTransfer>(
        'destinationId',
        setSearchText,
        searchText,
        searchInput,
      ),
      render: (_: any, item: InternalTransfer) => (
        <Text style={{ fontSize: 18 }}>
          {item.destinationType === 'wallet' ? `Wallet ${item.destinationCurrency}` : `MT ${item.destinationId}`}
        </Text>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 130,
      sorter: true,
      render: (_: any, item: InternalTransfer) => (
        <Text style={{ fontSize: 18, fontWeight: 500 }}>
          {formatDigits(Number(item.sourceAmount))} {item.sourceCurrency}
        </Text>
      ),
    },
    {
      title: 'Updated',
      dataIndex: 'humanUpdatedAt',
      key: 'humanUpdatedAt',
      width: 130,
      render: (v: string) => (
        <Text style={{ fontSize: 18 }}>{v}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 130,
      defaultFilteredValue: ['waiting_for_approve', 'pending_rollback'],
      filters: [
        internalTransferStatus.invalid(),
        internalTransferStatus.waitingForApprove(),
        internalTransferStatus.rejected(),
        internalTransferStatus.awaitingDebiting(),
        internalTransferStatus.inDebiting(),
        internalTransferStatus.awaitingCrediting(),
        internalTransferStatus.inCrediting(),
        internalTransferStatus.pendingRollback(),
        internalTransferStatus.rollingBack(),
        internalTransferStatus.completed(),
        internalTransferStatus.failed(),
      ].map((s) => ({
        text: humanInternalTransferStatus(s),
        value: s,
      })),
      filterSearch: true,
      onFilter: (value: any, record) => record.status == value,
      render: (item: string) => (
        <Tag color={colorInternalTransferStatus(item)} style={{ fontSize: 11 }}>
          {humanInternalTransferStatus(item)}
        </Tag>
      ),
    },
  ]

  return (
    <div>
      {/* CSV export */}
      <Guard permission="downloads_transactions">
        <Flex justify="flex-end" style={{ marginBottom: 10 }}>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            href={csvCode}
            download="internal-transfers.csv"
            target="_blank"
          >
            CSV
          </Button>
        </Flex>
      </Guard>

      {/* Split view: table + detail */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: selectedRecord ? '1fr 500px' : '1fr',
          gap: 0,
        }}
      >
        {/* Table */}
        <div
          style={{
            background: 'var(--crm-bg)',
            border: '1px solid var(--crm-border)',
            borderRadius: selectedRecord ? '8px 0 0 8px' : 8,
            overflow: 'hidden',
          }}
        >
          <Table
            dataSource={internalTransfers}
            columns={columns}
            size="small"
            scroll={{ x: 'max-content' }}
            loading={loading}
            rowKey="transferId"
            pagination={{
              pageSize,
              showSizeChanger: true,
              pageSizeOptions: [15, 30, 50, 100],
              total: totalCountOfInternalTransfers,
              showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
            }}
            onRow={(record) => ({
              onClick: () => setSelectedRecord(record),
              style: {
                cursor: 'pointer',
                background:
                  selectedRecord?.transferId === record.transferId
                    ? 'var(--crm-bg-elevated)'
                    : undefined,
              },
            })}
            onChange={(pagination, filters, sorter) =>
              handleTableChange({
                // @ts-ignore
                currentPage: pagination.current,
                // @ts-ignore
                currentPageSize: pagination.pageSize,
                // @ts-ignore
                currentOrderDirection: sorter.order,
                // @ts-ignore
                currentOrderBy: sorter.field,
                filters,
              })
            }
          />
        </div>

        {/* Detail panel */}
        {selectedRecord && (
          <div
            style={{
              background: 'var(--crm-bg)',
              borderTop: '1px solid var(--crm-border)',
              borderRight: '1px solid var(--crm-border)',
              borderBottom: '1px solid var(--crm-border)',
              borderRadius: '0 8px 8px 0',
              overflowY: 'auto',
              maxHeight: 'calc(100vh - 150px)',
            }}
          >
            <DetailPanel record={selectedRecord} />
          </div>
        )}
      </div>
    </div>
  )
}
